import type { MergedWorkspace } from '@/features/workspaces';
import {
  allEnabledIdentifiers,
  enabledIdentifiers,
  IdentifierKey,
  IdentifierType,
  isEmailIdentified,
  isIdentifierEnabled,
} from '@/features/workspaces';
import type { identifySnippet } from './snippets';

export type CustomerioPageModel = {
  workspaceId: string;
  workspaceName: string;
  /** Everything the identify example needs to fit this workspace. */
  identify: Parameters<typeof identifySnippet>[0];
  /** Every enabled identifier, `cio_id` included, in wire order. */
  allEnabledIdentifiers: readonly string[];
  /** The user-facing identifiers, which decide the singular or plural wording. */
  enabledIdentifierCount: number;
  isMultiIdentifier: boolean;
};

/**
 * The workspace facts the page renders, read off the resolved workspace.
 *
 * The route view holds a loader until the workspace resolves, so this only sees
 * a workspace; the identifiers map is still optional, because the synthetic
 * sample workspace does not carry one.
 */
export function buildCustomerioPageModel(workspace: MergedWorkspace): CustomerioPageModel {
  const identifiers = workspace.identifiers;
  const isMultiIdentifier = workspace.identifier_type === IdentifierType.MultiIdentifier;

  return {
    workspaceId: String(workspace.id),
    workspaceName: workspace.name,
    identify: {
      isEmailIdentified: isEmailIdentified(workspace.identifier_type),
      isMultiIdentifier,
      canIdentifyByEmail: isIdentifierEnabled(identifiers?.[IdentifierKey.Email]),
      allEnabledIdentifiers: allEnabledIdentifiers(identifiers),
    },
    allEnabledIdentifiers: allEnabledIdentifiers(identifiers),
    enabledIdentifierCount: enabledIdentifiers(identifiers).length,
    isMultiIdentifier,
  };
}
